import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface ChartOfAccountsModel {
  id?: string;
  konto: string;
  nazivKonta: string;
  selected?: boolean;
  clientID?: string;
  searchKeyword?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChartOfAccountsDataService {
  constructor(private snackBar: MatSnackBar) {}

  private COA_DATA = new BehaviorSubject<ChartOfAccountsModel[]>([]);
  private dataStore: { accounts: ChartOfAccountsModel[] } = { accounts: [] };
  readonly accounts = this.COA_DATA.asObservable();
  private nextToken: string | undefined;

  resetData() {
    this.dataStore.accounts = [];
    this.COA_DATA.next([]);
  }

  async listByClientID(
    clientID: string,
    filter?: any,
    limit?: number,
    nextToken?: string
  ): Promise<{ items: ChartOfAccountsModel[]; nextToken: string | null }> {
    const statement = `query GetChartOfAccountsByClientID($clientID: ID!, $filter: ModelChartOfAccountsFilterInput, $limit: Int, $nextToken: String) {
      getChartOfAccountsByClientID(clientID: $clientID, filter: $filter, limit: $limit, nextToken: $nextToken) {
        items {
          id
          konto
          nazivKonta
          clientID
          createdAt
          updatedAt
        }
        nextToken
      }
    }`;
    const gqlAPIServiceArguments: any = {
      clientID,
      limit: limit || 50,
    };
    if (filter) gqlAPIServiceArguments.filter = filter;
    if (nextToken) gqlAPIServiceArguments.nextToken = nextToken;

    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return response.data.getChartOfAccountsByClientID;
  }

  async listAllByClientID(clientID: string): Promise<ChartOfAccountsModel[]> {
    let allItems: ChartOfAccountsModel[] = [];
    let token: string | null = null;
    do {
      const result: any = await this.listByClientID(clientID, undefined, 10000, token || undefined);
      allItems = allItems.concat(result.items);
      token = result.nextToken;
    } while (token);
    return allItems;
  }

  async createChartOfAccounts(input: ChartOfAccountsModel): Promise<ChartOfAccountsModel> {
    const statement = `mutation CreateChartOfAccounts($input: CreateChartOfAccountsInput!) {
      createChartOfAccounts(input: $input) {
        id
        konto
        nazivKonta
        clientID
        createdAt
        updatedAt
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.createChartOfAccounts;
  }

  async updateChartOfAccounts(input: Partial<ChartOfAccountsModel> & { id: string }): Promise<ChartOfAccountsModel> {
    const statement = `mutation UpdateChartOfAccounts($input: UpdateChartOfAccountsInput!) {
      updateChartOfAccounts(input: $input) {
        id
        konto
        nazivKonta
        clientID
        createdAt
        updatedAt
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.updateChartOfAccounts;
  }

  async deleteChartOfAccounts(id: string): Promise<void> {
    const statement = `mutation DeleteChartOfAccounts($input: DeleteChartOfAccountsInput!) {
      deleteChartOfAccounts(input: $input) {
        id
      }
    }`;
    await API.graphql(graphqlOperation(statement, { input: { id } }));
  }

  async bulkImport(csvUrl: string): Promise<any> {
    const statement = `mutation BulkChartOfAccountsImport($input: ModelBulkChartOfAccountsImportInput!) {
      bulkChartOfAccountsImport(input: $input) {
        result
        message
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input: { csvUrl } })
    )) as any;
    return response?.data?.bulkChartOfAccountsImport ?? { result: 'SUCCESS' };
  }

  async initializeData(clientID: string): Promise<void> {
    const data = await this.listByClientID(clientID, undefined, 50)
      .catch((err) => { console.log(err); return { items: [], nextToken: null }; });
    this.dataStore.accounts = data.items;
    this.COA_DATA.next([...this.dataStore.accounts]);
    if ((data as any).nextToken) {
      this.nextToken = (data as any).nextToken;
    } else {
      this.nextToken = undefined;
    }
  }

  async filterAccounts(clientID: string, keyword: string): Promise<void> {
    this.dataStore.accounts = [];
    let token: string | null = null;
    do {
      const data: any = await this.listByClientID(
        clientID,
        { searchKeyword: { contains: keyword } },
        100000,
        token || undefined
      ).catch((err) => { console.log(err); return null; });
      if (data) {
        this.dataStore.accounts = [...this.dataStore.accounts, ...data.items];
        this.COA_DATA.next([...this.dataStore.accounts]);
        token = data.nextToken;
      } else {
        token = null;
      }
    } while (token);
  }

  async fetchAdditional(clientID: string): Promise<void> {
    if (!this.nextToken) return;
    const data = await this.listByClientID(clientID, undefined, 150, this.nextToken)
      .catch((err) => { console.log(err); return { items: [], nextToken: null }; });
    this.dataStore.accounts = this.dataStore.accounts.concat(data.items);
    this.COA_DATA.next([...this.dataStore.accounts]);
    this.nextToken = (data as any).nextToken || undefined;
  }

  async addAccount(account: ChartOfAccountsModel): Promise<void> {
    const user = await Auth.currentAuthenticatedUser();
    const input: ChartOfAccountsModel = {
      clientID: user?.attributes.sub,
      konto: account.konto,
      nazivKonta: account.nazivKonta,
    };
    const created = await this.createChartOfAccounts(input);
    this.dataStore.accounts.unshift(created);
    this.COA_DATA.next([...this.dataStore.accounts]);
  }

  async editAccount(accountData: ChartOfAccountsModel): Promise<void> {
    const found = this.dataStore.accounts.find((a) => a.id === accountData.id);
    if (found) {
      const updated = await this.updateChartOfAccounts({ id: accountData.id!, konto: accountData.konto, nazivKonta: accountData.nazivKonta });
      Object.assign(found, updated);
      this.COA_DATA.next([...this.dataStore.accounts]);
      this.snackBar.open('Changes successfully saved', '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }

  async removeAccount(item: ChartOfAccountsModel): Promise<void> {
    const found = this.dataStore.accounts.find((a) => a.id === item.id);
    if (found) {
      this.dataStore.accounts = this.dataStore.accounts.filter((a) => a.id !== item.id);
      this.COA_DATA.next([...this.dataStore.accounts]);
      await this.deleteChartOfAccounts(item.id!).catch((err) => console.log(err));
      this.snackBar.open(`The "${item.konto}" account has been deleted`, '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }
}
